<script setup>
import { onMounted, ref } from 'vue';
import otpChannel from 'dashboard/api/channel/otpChannel';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const deliveries = ref([]);
const sessions = ref([]);
const loading = ref(false);

const formatDate = value => new Date(value).toLocaleString();

const statusClass = status =>
  ({
    sent: 'text-n-teal-11',
    failed: 'text-n-ruby-11',
    pending: 'text-n-amber-11',
    connected: 'text-n-teal-11',
    qr: 'text-n-amber-11',
    connecting: 'text-n-sky-11',
    error: 'text-n-ruby-11',
    disconnected: 'text-n-slate-11',
    idle: 'text-n-slate-11',
  })[status] || 'text-n-slate-11';

async function load() {
  loading.value = true;
  try {
    const [deliveriesRes, sessionsRes] = await Promise.all([
      otpChannel.getDeliveries(),
      otpChannel.getSessions(),
    ]);
    deliveries.value = deliveriesRes.data;
    sessions.value = sessionsRes.data;
  } finally {
    loading.value = false;
  }
}

onMounted(load);
</script>

<template>
  <div class="flex flex-col gap-6 w-full max-w-5xl py-6">
    <div class="flex items-center justify-between gap-3">
      <div>
        <h2 class="text-lg font-semibold text-n-slate-12">
          {{ $t('OTP_MESSAGES.LOG_TITLE') }}
        </h2>
        <p class="text-sm text-n-slate-11">
          {{ $t('OTP_MESSAGES.LOG_DESCRIPTION') }}
        </p>
      </div>
    </div>

    <Spinner v-if="loading" class="size-8" />

    <div v-else class="flex flex-col gap-6">
      <div class="flex flex-col gap-3 rounded-xl border border-n-weak p-4">
        <h3 class="text-base font-medium text-n-slate-12">
          {{ $t('OTP_MESSAGES.SESSIONS') }}
        </h3>

        <div v-if="!sessions.length" class="text-sm text-n-slate-11">
          {{ $t('OTP_MESSAGES.NO_SESSIONS') }}
        </div>

        <table v-else class="w-full text-sm text-n-slate-12">
          <thead>
            <tr class="text-start text-n-slate-11 border-b border-n-weak">
              <th class="py-2 text-start font-medium">
                {{ $t('OTP_MESSAGES.SESSION_KEY') }}
              </th>
              <th class="py-2 text-start font-medium">
                {{ $t('OTP_MESSAGES.STATUS') }}
              </th>
              <th class="py-2 text-start font-medium">
                {{ $t('OTP_MESSAGES.PHONE_NUMBER') }}
              </th>
              <th class="py-2 text-start font-medium">
                {{ $t('OTP_MESSAGES.DATE') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="session in sessions"
              :key="session.id"
              class="border-b border-n-weak"
            >
              <td class="py-2 font-mono text-xs">{{ session.session_key }}</td>
              <td class="py-2">
                <span :class="statusClass(session.status)">{{
                  session.status
                }}</span>
              </td>
              <td class="py-2">{{ session.phone_number || '—' }}</td>
              <td class="py-2 text-n-slate-11">
                {{ formatDate(session.created_at) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="flex flex-col gap-3 rounded-xl border border-n-weak p-4">
        <h3 class="text-base font-medium text-n-slate-12">
          {{ $t('OTP_MESSAGES.DELIVERY_LOG') }}
        </h3>

        <div v-if="!deliveries.length" class="text-sm text-n-slate-11">
          {{ $t('OTP_MESSAGES.NO_DELIVERIES') }}
        </div>

        <table v-else class="w-full text-sm text-n-slate-12">
          <thead>
            <tr class="text-start text-n-slate-11 border-b border-n-weak">
              <th class="py-2 text-start font-medium">
                {{ $t('OTP_MESSAGES.PHONE_NUMBER') }}
              </th>
              <th class="py-2 text-start font-medium">
                {{ $t('OTP_MESSAGES.STATUS') }}
              </th>
              <th class="py-2 text-start font-medium">
                {{ $t('OTP_MESSAGES.ERROR') }}
              </th>
              <th class="py-2 text-start font-medium">
                {{ $t('OTP_MESSAGES.DATE') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="delivery in deliveries"
              :key="delivery.id"
              class="border-b border-n-weak"
            >
              <td class="py-2" dir="ltr">{{ delivery.phone_number }}</td>
              <td class="py-2">
                <span :class="statusClass(delivery.status)">{{
                  delivery.status
                }}</span>
              </td>
              <td class="py-2 text-n-ruby-11">
                {{ delivery.error_message || '—' }}
              </td>
              <td class="py-2 text-n-slate-11">
                {{ formatDate(delivery.created_at) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>
